import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Item, ItemStatus, Location, Category } from '@/types';
import { Checkbox } from '@/components/ui/checkbox';
import { apiService } from '@/services/api';
import toast from 'react-hot-toast';

const itemSchema = z.object({
  name: z.string().min(1, 'Name ist erforderlich'),
  description: z.string().optional(),
  sku: z.string().optional(),
  quantity: z.coerce.number().min(0, 'Menge muss 0 oder größer sein'),
  minQuantity: z.coerce.number().min(0, 'Mindestmenge muss 0 oder größer sein'),
  status: z.nativeEnum(ItemStatus),
  locationId: z.string().optional(),
  categoryId: z.string().optional(),
  isRental: z.coerce.boolean().default(false),
  serialNumber: z.string().optional(),
  isManageInSAP: z.coerce.boolean().default(false),
  sapPlantNumbers: z.array(z.union([z.string(), z.number()]).transform(val => String(val))).default([]),
}).refine((data) => {
  // Wenn es ein Mietgerät ist, muss eine Seriennummer angegeben werden
  if (data.isRental && (!data.serialNumber || data.serialNumber.trim() === '')) {
    return false;
  }
  return true;
}, {
  message: 'Seriennummer ist für Mietgeräte erforderlich',
  path: ['serialNumber'],
});

type ItemFormData = z.infer<typeof itemSchema>;

interface ItemFormProps {
  item?: Item | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (item: Item) => void;
}

const ItemForm: React.FC<ItemFormProps> = ({
  item,
  isOpen,
  onClose,
  onSave,
}) => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [, setCategories] = useState<Category[]>([]);
  const [flatCategories, setFlatCategories] = useState<Category[]>([]);
  const [plantNumbers, setPlantNumbers] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ItemFormData>({
    resolver: zodResolver(itemSchema),
    defaultValues: {
      name: item?.name || '',
      description: item?.description || '',
      sku: item?.sku || '',
      quantity: item?.quantity || 0,
      minQuantity: item?.minQuantity || 0,
      status: item?.status || ItemStatus.AVAILABLE,
      locationId: item?.locationId || '',
      categoryId: item?.categoryId || '',
      isRental: item?.isRental || false,
      serialNumber: item?.serialNumber || '',
      isManageInSAP: item?.isManageInSAP || false,
      sapPlantNumbers: item?.sapPlantNumbers || [],
    },
  });

  const isRental = watch('isRental');
  const isManageInSAP = watch('isManageInSAP');

  // Setze Menge automatisch für Mietgeräte
  useEffect(() => {
    if (isRental) {
      setValue('quantity', 1);
      setValue('minQuantity', 0);
    }
  }, [isRental, setValue]);

  // Erstelle hierarchische Anzeige für Standorte
  const getLocationPath = (locationId: string): string[] => {
    const path: string[] = [];
    let currentId: string | undefined = locationId;
    
    while (currentId) {
      const location = locations.find(loc => loc.id === currentId);
      if (!location) break;
      path.unshift(location.name);
      currentId = location.parentId;
    }
    
    return path;
  };

  const getLocationDisplayName = (loc: Location): string => {
    const path = getLocationPath(loc.id);
    const indent = '  '.repeat(path.length - 1);
    
    if (path.length === 1) {
      return `${indent}📍 ${loc.name}${loc.plantNumber ? ` (${loc.plantNumber})` : ''}`;
    } else {
      const parentPath = path.slice(0, -1).join(' > ');
      return `${indent}📍 ${loc.name} (${parentPath})${loc.plantNumber ? ` [${loc.plantNumber}]` : ''}`;
    }
  };

  // Funktion zum Rendern der Kategorie-Optionen mit Hierarchie
  const renderCategoryOptions = (): React.ReactNode[] => {
    // Erstelle eine Map für Parent-Zuordnung
    const categoryMap = new Map<string, Category>();
    flatCategories.forEach(cat => categoryMap.set(cat.id, cat));
    
    // Berechne die Hierarchie-Tiefe für jede Kategorie
    const getCategoryLevel = (categoryId: string): number => {
      const category = categoryMap.get(categoryId);
      if (!category?.parentId) return 0;
      return getCategoryLevel(category.parentId) + 1;
    };
    
    // Sortiere Kategorien hierarchisch
    const sortedCategories = [...flatCategories].sort((a, b) => {
      // Erst nach Hierarchie-Pfad, dann alphabetisch
      const getHierarchyPath = (categoryId: string): string => {
        const category = categoryMap.get(categoryId);
        if (!category?.parentId) return category?.name || '';
        return getHierarchyPath(category.parentId) + '/' + category.name;
      };
      
      return getHierarchyPath(a.id).localeCompare(getHierarchyPath(b.id));
    });
    
    return sortedCategories.map(category => {
      const level = getCategoryLevel(category.id);
      const prefix = '  '.repeat(level) + (level > 0 ? '└─ ' : '');
      
      return (
        <option key={category.id} value={category.id}>
          {prefix + category.name}
        </option>
      );
    });
  };

  useEffect(() => {
    if (isOpen) {
      fetchLocationsAndCategories();
    }
  }, [isOpen]);

  useEffect(() => {
    // Reset form nur wenn Daten geladen wurden
    if (isOpen && (locations.length > 0 || flatCategories.length > 0)) {
      if (item) {
        console.log('ItemForm: Resetting form with item:', item);
        console.log('ItemForm: Available locations:', locations);
        console.log('ItemForm: Available categories:', flatCategories);
        console.log('ItemForm: Available plant numbers:', plantNumbers);
        console.log('ItemForm: Item locationId:', item.locationId);
        console.log('ItemForm: Item categoryId:', item.categoryId);
        console.log('ItemForm: Item isManageInSAP:', item.isManageInSAP);
        console.log('ItemForm: Item sapPlantNumbers:', item.sapPlantNumbers);
        console.log('ItemForm: Item sapPlantNumbers type:', typeof item.sapPlantNumbers);
        
        // Stelle sicher, dass sapPlantNumbers ein Array ist
        const sapPlantNumbers = Array.isArray(item.sapPlantNumbers) 
          ? item.sapPlantNumbers 
          : (item.sapPlantNumbers ? [item.sapPlantNumbers] : []);
        
        console.log('ItemForm: Processed sapPlantNumbers:', sapPlantNumbers);
        
        reset({
          name: item.name,
          description: item.description || '',
          sku: item.sku || '',
          quantity: item.quantity,
          minQuantity: item.minQuantity,
          status: item.status,
          locationId: item.locationId || '',
          categoryId: item.categoryId || '',
          isRental: item.isRental || false,
          serialNumber: item.serialNumber || '',
          isManageInSAP: item.isManageInSAP || false,
          sapPlantNumbers: sapPlantNumbers,
        });
      } else {
        reset({
          name: '',
          description: '',
          sku: '',
          quantity: 0,
          minQuantity: 0,
          status: ItemStatus.AVAILABLE,
          locationId: '',
          categoryId: '',
          isRental: false,
          serialNumber: '',
          isManageInSAP: false,
          sapPlantNumbers: [],
        });
      }
    }
  }, [isOpen, item, reset, locations, flatCategories, plantNumbers]);

  const fetchLocationsAndCategories = async () => {
    try {
      const [locationsData, categoriesData, plantNumbersData] = await Promise.all([
        apiService.get<Location[]>('/locations'),
        apiService.get<Category[]>('/categories'),
        apiService.get<string[]>('/locations/plant-numbers'),
      ]);
      setLocations(locationsData || []);
      setPlantNumbers(plantNumbersData || []);
      
      // Extrahiere alle Kategorien flach aus der Hierarchie für das Dropdown
      const flattenAllCategories = (cats: Category[]): Category[] => {
        const result: Category[] = [];
        const addCategory = (cat: Category) => {
          result.push(cat);
          if (cat.children && cat.children.length > 0) {
            cat.children.forEach(child => addCategory(child));
          }
        };
        cats.forEach(cat => addCategory(cat));
        return result;
      };
      
      const hierarchicalCategories = categoriesData || [];
      const flatCategoriesList = flattenAllCategories(hierarchicalCategories);
      
      console.log('ItemForm: Hierarchical categories:', hierarchicalCategories);
      console.log('ItemForm: Flat categories for dropdown:', flatCategoriesList);
      console.log('ItemForm: Plant numbers:', plantNumbersData);
      
      setCategories(hierarchicalCategories);
      setFlatCategories(flatCategoriesList);
    } catch (error) {
      console.error('Error fetching locations/categories/plant numbers:', error);
      toast.error('Fehler beim Laden der Standorte/Kategorien/Werknummern');
    }
  };

  const onSubmit = async (data: ItemFormData) => {
    setLoading(true);
    console.log('ItemForm: Submitting form data:', data);
    console.log('ItemForm: SAP Plant Numbers being submitted:', data.sapPlantNumbers);
    console.log('ItemForm: Data types:', {
      quantity: typeof data.quantity,
      minQuantity: typeof data.minQuantity,
      isRental: typeof data.isRental,
      isManageInSAP: typeof data.isManageInSAP,
      sapPlantNumbers: typeof data.sapPlantNumbers,
      sapPlantNumbersArray: Array.isArray(data.sapPlantNumbers)
    });
    
    try {
      let savedItem: Item;
      
      if (item) {
        // Update existing item
        console.log('ItemForm: Updating item with ID:', item.id);
        savedItem = await apiService.put<Item>(`/items/${item.id}`, data);
        console.log('ItemForm: Item updated successfully:', savedItem);
        toast.success('Item erfolgreich aktualisiert');
      } else {
        // Create new item
        console.log('ItemForm: Creating new item');
        savedItem = await apiService.post<Item>('/items', data);
        console.log('ItemForm: Item created successfully:', savedItem);
        toast.success('Item erfolgreich erstellt');
      }
      
      onSave(savedItem);
      onClose();
    } catch (error: any) {
      console.error('Error saving item:', error);
      console.error('Error details:', error.response?.data);
      const errorMessage = error.response?.data?.error || error.message || 'Fehler beim Speichern des Items';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {item ? 'Item bearbeiten' : 'Neues Item erstellen'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit, (errors) => {
          console.error('ItemForm: Validation errors:', errors);
          toast.error('Validierungsfehler: Bitte überprüfen Sie Ihre Eingaben');
        })} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                {...register('name')}
                placeholder="Item Name"
              />
              {errors.name && (
                <p className="text-sm text-red-500">{errors.name.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="sku">{isManageInSAP ? 'SAP Mat. Nr' : 'Mat. Nr'}</Label>
              <Input
                id="sku"
                {...register('sku')}
                placeholder={isManageInSAP ? 'SAP Material Nummer' : 'Material Nummer'}
              />
              {errors.sku && (
                <p className="text-sm text-red-500">{errors.sku.message}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Beschreibung</Label>
            <Input
              id="description"
              {...register('description')}
              placeholder="Item Beschreibung"
            />
            {errors.description && (
              <p className="text-sm text-red-500">{errors.description.message}</p>
            )}
          </div>

          {/* SAP Integration */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="isManageInSAP"
              checked={isManageInSAP}
              onCheckedChange={(checked: boolean | 'indeterminate') => setValue('isManageInSAP', checked === true)}
            />
            <Label htmlFor="isManageInSAP" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              In SAP geführt
            </Label>
          </div>
          {errors.isManageInSAP && (
            <p className="text-sm text-red-500">{errors.isManageInSAP.message}</p>
          )}

          {/* SAP Werknummern - nur anzeigen wenn SAP aktiviert */}
          {isManageInSAP && (
            <div className="space-y-2">
              <Label htmlFor="sapPlantNumbers">SAP Werknummern</Label>
              <select
                multiple
                value={watch('sapPlantNumbers') || []}
                onChange={(e) => {
                  const selectedOptions = Array.from(e.target.selectedOptions, (option) => option.value);
                  console.log('ItemForm: SAP Plant Numbers selection changed:', selectedOptions);
                  setValue('sapPlantNumbers', selectedOptions);
                }}
                className="w-full px-3 py-2 border border-input rounded-md bg-background text-sm min-h-[120px]"
                size={Math.min(plantNumbers.length, 6)}
              >
                {plantNumbers.map((plantNumber) => (
                  <option 
                    key={plantNumber} 
                    value={plantNumber}
                    selected={watch('sapPlantNumbers')?.includes(plantNumber)}
                  >
                    Werk {plantNumber}
                  </option>
                ))}
              </select>
              {plantNumbers.length === 0 && (
                <p className="text-sm text-muted-foreground">Keine Werknummern verfügbar. Erstellen Sie zuerst Standorte mit Werknummern.</p>
              )}
              <div className="text-xs text-muted-foreground">
                Tipp: Halten Sie Ctrl/Cmd gedrückt, um mehrere Werknummern auszuwählen
              </div>
              {/* Anzeige der ausgewählten Werknummern */}
              {(watch('sapPlantNumbers') || []).length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {(watch('sapPlantNumbers') || []).map((plantNumber) => (
                    <span
                      key={plantNumber}
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-primary text-primary-foreground"
                    >
                      Werk {plantNumber}
                      <button
                        type="button"
                        onClick={() => {
                          const currentPlantNumbers = watch('sapPlantNumbers') || [];
                          const newPlantNumbers = currentPlantNumbers.filter(pn => pn !== plantNumber);
                          console.log('ItemForm: Removing plant number:', plantNumber, 'New list:', newPlantNumbers);
                          setValue('sapPlantNumbers', newPlantNumbers);
                        }}
                        className="ml-1 hover:bg-primary-foreground/20 rounded-full p-0.5"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              {errors.sapPlantNumbers && (
                <p className="text-sm text-red-500">{errors.sapPlantNumbers.message}</p>
              )}
            </div>
          )}

          {/* Mietgeräte Checkbox */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="isRental"
              checked={isRental}
              onCheckedChange={(checked: boolean | 'indeterminate') => setValue('isRental', checked === true)}
            />
            <Label htmlFor="isRental" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              Mietgerät
            </Label>
          </div>
          {errors.isRental && (
            <p className="text-sm text-red-500">{errors.isRental.message}</p>
          )}

          {/* Seriennummer für Mietgeräte */}
          {isRental && (
            <div className="space-y-2">
              <Label htmlFor="serialNumber">Seriennummer *</Label>
              <Input
                id="serialNumber"
                {...register('serialNumber')}
                placeholder="Seriennummer des Mietgeräts"
              />
              {errors.serialNumber && (
                <p className="text-sm text-red-500">{errors.serialNumber.message}</p>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="quantity">Aktuelle Menge *</Label>
              <Input
                id="quantity"
                type="number"
                {...register('quantity', { valueAsNumber: true })}
                min="0"
                disabled={isRental}
              />
              {isRental && (
                <p className="text-xs text-muted-foreground">Mietgeräte haben automatisch die Menge 1</p>
              )}
              {errors.quantity && (
                <p className="text-sm text-red-500">{errors.quantity.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="minQuantity">Mindestmenge *</Label>
              <Input
                id="minQuantity"
                type="number"
                {...register('minQuantity', { valueAsNumber: true })}
                min="0"
                disabled={isRental}
              />
              {isRental && (
                <p className="text-xs text-muted-foreground">Mietgeräte haben automatisch die Mindestmenge 0</p>
              )}
              {errors.minQuantity && (
                <p className="text-sm text-red-500">{errors.minQuantity.message}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="status">Status *</Label>
              <select
                id="status"
                {...register('status')}
                className="w-full px-3 py-2 border border-input rounded-md bg-background text-sm"
              >
                <option value={ItemStatus.AVAILABLE}>Verfügbar</option>
                <option value={ItemStatus.CHECKED_OUT}>Ausgeliehen</option>
                <option value={ItemStatus.MAINTENANCE}>Wartung</option>
                <option value={ItemStatus.RETIRED}>Außer Betrieb</option>
              </select>
              {errors.status && (
                <p className="text-sm text-red-500">{errors.status.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="locationId">Lagerstandort</Label>
              <select
                id="locationId"
                {...register('locationId')}
                className="w-full px-3 py-2 border border-input rounded-md bg-background text-sm"
              >
                <option value="">Lagerstandort auswählen</option>
                {locations
                  .sort((a, b) => {
                    // Sortiere nach Hierarchie-Level (Hauptstandorte zuerst), dann alphabetisch
                    const aPath = getLocationPath(a.id);
                    const bPath = getLocationPath(b.id);
                    
                    if (aPath.length !== bPath.length) {
                      return aPath.length - bPath.length;
                    }
                    
                    return a.name.localeCompare(b.name);
                  })
                  .map((location) => (
                    <option key={location.id} value={location.id}>
                      {getLocationDisplayName(location)}
                    </option>
                  ))}
              </select>
              {errors.locationId && (
                <p className="text-sm text-red-500">{errors.locationId.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="categoryId">Kategorie</Label>
              <select
                id="categoryId"
                {...register('categoryId')}
                className="w-full px-3 py-2 border border-input rounded-md bg-background text-sm"
              >
                <option value="">Kategorie auswählen</option>
                {renderCategoryOptions()}
              </select>
              {errors.categoryId && (
                <p className="text-sm text-red-500">{errors.categoryId.message}</p>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Abbrechen
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Speichern...' : (item ? 'Aktualisieren' : 'Erstellen')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ItemForm;
